import { createForm } from "@tanstack/solid-form";
import { marked } from "marked";
import { createMemo, createSignal } from "solid-js";
import { useI18n } from "@/api/i18n";
import RichTextAreaField from "@/components/shared/ckeditor/Editor";
import { Button } from "@/components/ui/button";
import { FieldContainer, FieldInfo } from "@/components/ui/input";
import { useRefineStore } from "../../store/RefineProvider";
import { descriptionEditSchema } from "../../validators";
import { RefineEditDialog } from "../RefineEditDialog";
import { RefineStepCard } from "../RefineStepCard";

export function DescriptionStep() {
  const { t } = useI18n();
  const { state, updateFields } = useRefineStore();
  const [editOpen, setEditOpen] = createSignal(false);

  const renderedDescription = createMemo(() =>
    state.description ? marked.parse(state.description, { async: false }) : "—",
  );

  const form = createForm(() => ({
    defaultValues: {
      description: state.description,
    },
    onSubmit: ({ value }) => {
      const result = descriptionEditSchema.safeParse(value);
      if (!result.success) return;
      updateFields({ description: result.data.description });
      setEditOpen(false);
    },
  }));

  const handleOpenEdit = () => {
    form.reset();
    const html = state.description
      ? marked.parse(state.description, { async: false })
      : "";
    form.setFieldValue("description", html);
    setEditOpen(true);
  };

  return (
    <>
      <RefineStepCard
        title={t("jobOffers.refine.steps.description")}
        onEdit={handleOpenEdit}
      >
        <div
          class="prose prose-sm max-w-none text-gray-700"
          innerHTML={renderedDescription()}
        />
      </RefineStepCard>

      <RefineEditDialog
        open={editOpen()}
        class="max-w-4xl"
        onOpenChange={setEditOpen}
        title={t("jobOffers.refine.editDescription")}
      >
        <div class="space-y-4">
          <form.Field
            name="description"
            validators={{
              onChange: descriptionEditSchema.shape.description,
            }}
          >
            {(field) => (
              <FieldContainer>
                <RichTextAreaField
                  ref={() => {}}
                  name={field().name}
                  class="ck-custom-editor prose max-w-none w-full!"
                  value={field().state.value ?? ""}
                  onChange={(e) => field().handleChange(e.currentTarget.value)}
                  onInput={(e) => field().handleChange(e.currentTarget.value)}
                  onBlur={field().handleBlur}
                  rows={10}
                />
                <FieldInfo field={field()} />
              </FieldContainer>
            )}
          </form.Field>
          <div class="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={form.handleSubmit}>{t("common.save")}</Button>
          </div>
        </div>
      </RefineEditDialog>
    </>
  );
}
